import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Checkbox,
  Space,
  Table,
  InputNumber,
  Col,
  Row,
  Radio,
  Input,
  Select,
} from "antd";

import TechBaseText from "DisplayCoreV2/Text";
import TechBaseButton from "DisplayCoreV2/Button";
import TechbaseMemberFinder from "DisplayCoreV2/MemberFinder";
import IntendedUserInfo from "../IntendedUserInfo";
import { requireRule } from "utils/rules";
import { validateChineseRule, ValidateICRule } from "utils/rules";
import { relationshipOptions } from "components/Options";

const urnListColumns = [
  {
    title: <TechBaseText content="{{NUMBER}}" />,
    render: (text, record, index) => index + 1,
  },
  {
    title: <TechBaseText content="{{PRODUCT_CODE}}" />,
    dataIndex: "ProductCode",
    key: "ProductCode",
  },
  {
    title: <TechBaseText content="{{PRODUCT_NAME}}" />,
    dataIndex: "ProductName",
    key: "ProductName",
  },
  {
    title: <TechBaseText content="{{PRICE}}" />,
    dataIndex: "Price",
    key: "Price",
  },
  {
    title: <TechBaseText content="{{CATEGORY}}" />,
    dataIndex: "ProductCategoryName",
    key: "ProductCategoryName",
  },
  {
    title: <TechBaseText content="{{PLACE_ORDER}}" />,
    key: "action",
    render: (text, record) => (
      <Form.Item className="m-0" name={["items", record.ID]}>
        <InputNumber min={0} max={5} />
      </Form.Item>
    ),
  },
];

const Step1 = (props) => {
  const {
    profile,
    selectedSlot,
    onNext,
    formData,
    productList,
    salesType,
    bookingOrder,
    newCustomer,
    toggleCreateCustomer,
    setNewCustomer,
    userCapacity,
    genderOptions,
    index,
    subAdminRoles = []
  } = props;
  const [form] = Form.useForm();
  const intendedUser1 = Form.useWatch("intended_user_1", form);
  const intendedUser2 = Form.useWatch("intended_user_2", form);
  const [intendedUsers, setIntendedUsers] = useState({});
  const purchaser = Form.useWatch("purchaserID", form);

  // Extract role codes from subAdminRoles
  const subAdminRoleCodes = React.useMemo(() => {
    return (subAdminRoles || []).map(role => role.RoleCode);
  }, [subAdminRoles]);

  useEffect(() => {
    if (purchaser && purchaser.Fullname) {
      form.setFieldsValue({
        purchaserFullname: purchaser.Fullname,
      });
    }
  }, [purchaser]);

  const handleCheckboxChange = (userIndex, checked) => {
    setIntendedUsers((prev) => ({
      ...prev,
      [`intendedUser${userIndex}`]: checked,
    }));

    // ✅ Ensure form field updates properly
    form.setFieldsValue({ [`intended_user_${userIndex}`]: checked });
  };
  useEffect(() => {
    setIntendedUsers({
      intendedUser1: intendedUser1 || false,
      intendedUser2: intendedUser2 || false,
    });
  }, [intendedUser1, intendedUser2]);

  useEffect(() => {
    if (newCustomer) {
      form.setFieldValue("purchaserID", newCustomer);
      setNewCustomer("");
    }
  }, [newCustomer]);

  useEffect(() => {
    if (bookingOrder?.PurchaserID || formData?.purchaserID) {
      form.setFieldsValue({
        purchaserID: bookingOrder?.PurchaserID || formData.purchaserID,
        purchaserFullname: bookingOrder?.PurchaserName || formData.purchaserFullname,
        memberID: bookingOrder?.AgentID || formData.memberID,
        ...formData, // You can still spread other form values here safely
      });
    }
  }, [bookingOrder, formData]);


  useEffect(() => {
    if (selectedSlot.UnitID) {
      props.getUnitUserCapacity(selectedSlot.UnitID);
    }
  }, []);
  const onFinish = (value) => {
    // Extract all intended user details
    const userCapacityCount = (userCapacity && userCapacity.UserCapacity) || 1;
    const intendedUsersArray = [];

    for (let i = 1; i <= userCapacityCount; i++) {
      if (value[`intended_user_${i}`]) {
        intendedUsersArray.push({
          fullName: value[`intendedUser${i - 1}`]?.fullName,
          chineseName: value[`intendedUser${i - 1}`]?.chineseName,
          relationship: value[`intendedUser${i - 1}`]?.relationship,
          identityNo: value[`intendedUser${i - 1}`]?.identityNo,
          gender: value[`intendedUser${i - 1}`]?.gender,
        });
      }
    }

    onNext({
      ...value,
      intendedUsers: intendedUsersArray, // ✅ Add this
      purchaserID: value.purchaserID, // Should be the full object { MemberId, Fullname }
      memberID: value.memberID,
    });
  };

  return (
    <Form
      scrollToFirstError
      onFinish={onFinish}
      layout="vertical"
      form={form}
      initialValues={{
        memberID: bookingOrder?.AgentID,
        purchaserID: bookingOrder?.PurchaserID,
        needCondition: salesType !== "FSP" ? "PN" : undefined, // Set default value for needCondition
        ...formData,
      }}
    >
      <Row className="w-100 d-block ">
        <Card className="mb-3">
          {salesType === "FSP" ? (
            <div className="lead">
              <TechBaseText content="{{SELECTED_FSP}}" />:{" "}
              {selectedSlot.ProductName}
            </div>
          ) : salesType === "monument" ? (
            <div className="lead">
              <TechBaseText content="{{SELECTED_MONUMENT}}" />:{" "}
              {selectedSlot.ProductName}
            </div>
          ) : (
            <div className="lead">
              <TechBaseText content="{{LOT_NO}}" />: {selectedSlot.UnitID}
            </div>
          )}
        </Card>
        <Card className="mb-3">
          <div className="lead">
            <TechBaseText content="{{PERSONAL_INFORMATION}}" />
          </div>
          <Row gutter={32}>
            {salesType !== "FSP" && (
              <Col xs={24}>
                <Form.Item name="needCondition" rules={[requireRule]}>
                  <Radio.Group>
                    <Radio value="PN">
                      {" "}
                      <TechBaseText content="{{PRE_NEED}}" />{" "}
                    </Radio>
                    <Radio value="AN">
                      {" "}
                      <TechBaseText content="{{AS_NEED}}" />{" "}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item
                label={<TechBaseText content="{{AGENT_ID}}" />}
                rules={[requireRule]}
                name="memberID"
              >
                <TechbaseMemberFinder
                  role="M"
                  size="large"
                  placeholder={TechBaseText({ content: "{{AGENT_ID}}" })}
                  Search={profile.Role !== "AD" && !subAdminRoleCodes.includes(profile.Role)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<TechBaseText content="{{PURCHASER_ID}}" />}
                rules={[requireRule]}
                name="purchaserID"
              >
                <TechbaseMemberFinder
                  role="C"
                  size="large"
                  placeholder={TechBaseText({ content: "{{PURCHASER_ID}}" })}
                  disabledSearch={props.disabledPurchaserID}
                />
              </Form.Item>



              <TechBaseButton
                size="large"
                style={{ width: "300px" }}
                onClick={toggleCreateCustomer}
                hidden={props.hideCreateNewPurchaser}
              >
                <TechBaseText content="{{CREATE_NEW_PURCHASER}}" />
              </TechBaseButton>
            </Col>
          </Row>
        </Card>

        {salesType !== "FSP" && (
          <>
            <Card className="mb-3">
              <div className="lead">
                <TechBaseText content="{{URN_LIST}}" />
              </div>
              <Table
                bordered
                className="techbase-table"
                columns={urnListColumns}
                dataSource={productList}
                pagination={false}
              />
            </Card>
            <Card className="mb-3">
              <div className="lead">
                <TechBaseText content="{{ADD_INTENDED_USER}}" />
              </div>
              {Array.from(
                {
                  length:
                    props.userCapacity && props.userCapacity.UserCapacity
                      ? props.userCapacity.UserCapacity
                      : 1,
                },
                (_, index) => {
                  const userIndex = index + 1;
                  return (
                    <React.Fragment key={userIndex}>
                      <Form.Item
                        className="m-0"
                        name={`intended_user_${userIndex}`}
                        valuePropName="checked"
                      >
                        <Checkbox
                          onChange={(e) =>
                            handleCheckboxChange(userIndex, e.target.checked)
                          }
                        >
                          <TechBaseText content="{{USER}}" /> {userIndex}
                        </Checkbox>
                      </Form.Item>
                      {intendedUsers[`intendedUser${userIndex}`] && (
                        <>
                          <Card>
                            <div className="lead mb-2">
                              <TechBaseText content="{{INTENDED_USER_INFORMATION}}" />
                            </div>
                            <Row gutter={32}>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  name={[`intendedUser${index}`, "fullName"]}
                                  label={
                                    <TechBaseText content="{{FULL_NAME_AS_PER_IC}}" />
                                  }
                                >
                                  <Input
                                    size="large"
                                    placeholder={TechBaseText({
                                      content: "{{FULL_NAME}}",
                                    })}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  name={[`intendedUser${index}`, "chineseName"]}
                                  label={
                                    <TechBaseText content="{{CHINESE_NAME}}" />
                                  }
                                  rules={[validateChineseRule]}
                                >
                                  <Input
                                    size="large"
                                    placeholder={TechBaseText({
                                      content: "{{CHINESE_NAME}}",
                                    })}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  name={[
                                    `intendedUser${index}`,
                                    "relationship",
                                  ]}
                                  label={
                                    <TechBaseText content="{{RELATIONSHIP}}" />
                                  }
                                >
                                  <Select
                                    size="large"
                                    options={relationshipOptions}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  name={[`intendedUser${index}`, "identityNo"]}
                                  label={
                                    <TechBaseText content="{{IDENTITY_CARD_NUMBER}}" />
                                  }
                                  rules={[ValidateICRule()]}
                                >
                                  <Input
                                    size="large"
                                    placeholder={TechBaseText({
                                      content: "{{IDENTITY_CARD_NUMBER}}",
                                    })}
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} md={12}>
                                <Form.Item
                                  name={[`intendedUser${index}`, "gender"]}
                                  label={<TechBaseText content="{{GENDER}}" />}
                                >
                                  <Select
                                    size="large"
                                    options={genderOptions}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Card>
                        </>
                      )}
                    </React.Fragment>
                  );
                }
              )}
            </Card>
          </>
        )}

        <div className="techbase-button-wrapper-modal">
          <TechBaseButton
            className="techbase-button-modal"
            htmlType="submit"
            color="primary"
          >
            <TechBaseText content="{{NEXT}}" />
          </TechBaseButton>
        </div>
      </Row>
    </Form>
  );
};

export default Step1;
